// integrationsCatalog.js — Lista de integrações externas disponíveis pro
// Apolo IDE conectar. Logos via Simple Icons CDN.
//
// Sprint v9.8 — abre TODAS as integrações pra conexão. 3 níveis de status:
//   • 'available' (token-able): user cola API key/PAT, validamos
//   • 'oauth-soon' (só faz OAuth, sem token): mostra "OAuth em breve"
//   • 'local-only': app local sem token (Obsidian, Playwright, Granola)
//
// docsUrl/docsLabel/tokenLabel/tokenPrefix populam o form genérico
// TokenForm. validator no backend (apolo-api/src/routes/integrations.js):
//   - linear / supabase / cloudflare / github: validação real
//   - demais: stora sem validar (Pedro confia na key que colou)

const SI = (slug) => `https://cdn.simpleicons.org/${slug}`;
const SIc = (slug, hex) => `https://cdn.simpleicons.org/${slug}/${hex.replace('#', '')}`;

export const INTEGRATION_CATEGORIES = [
  { id: 'all', label: 'Todas' },
  { id: 'ai', label: 'IA & Modelos' },
  { id: 'dev', label: 'Dev & Code' },
  { id: 'productivity', label: 'Produtividade' },
  { id: 'communication', label: 'Comunicação' },
  { id: 'design', label: 'Design' },
  { id: 'data', label: 'Dados & Analytics' },
  { id: 'sales', label: 'Vendas & CRM' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'voice', label: 'Voz & Reuniões' },
];

export const INTEGRATIONS = [
  // ── AI & MODELOS ───────────────────────────────────────────────────────
  // Sprint v9.15 — LLM providers (espelha MODEL_REGISTRY). Validators reais
  // no Worker. Save propaga pro user_settings cache pra chat.js + voice.js
  // (Whisper) encontrarem imediatamente.
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4o, GPT-5, Whisper voice, DALL-E · obrigatório pra Whisper',
    category: 'ai',
    iconUrl: SI('openai'),
    status: 'available',
    tokenLabel: 'API Key',
    tokenPlaceholder: 'sk-proj-... ou sk-...',
    tokenPrefix: 'sk-',
    docsUrl: 'https://platform.openai.com/api-keys',
    docsLabel: 'Settings → API Keys → Create new secret key',
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude Opus 4.7, Sonnet 4.6',
    category: 'ai',
    iconUrl: SIc('anthropic', '#cc785c'),
    status: 'available',
    tokenLabel: 'API Key',
    tokenPlaceholder: 'sk-ant-api03-...',
    tokenPrefix: 'sk-ant-',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    docsLabel: 'Console → Settings → API Keys → Create Key',
  },
  {
    id: 'google-ai',
    name: 'Google AI Studio',
    description: 'Gemini 2.5 Pro, Flash · multimodal',
    category: 'ai',
    iconUrl: SIc('googlegemini', '#4285f4'),
    status: 'available',
    tokenLabel: 'API Key',
    tokenPlaceholder: 'AIzaSy...',
    tokenPrefix: 'AIza',
    docsUrl: 'https://aistudio.google.com/apikey',
    docsLabel: 'AI Studio → Get API key',
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    description: 'V3, R1 reasoning · chinês competitivo',
    category: 'ai',
    iconUrl: SIc('deepseek', '#4d6bfe'),
    status: 'available',
    tokenLabel: 'API Key',
    tokenPlaceholder: 'sk-...',
    tokenPrefix: 'sk-',
    docsUrl: 'https://platform.deepseek.com/api_keys',
    docsLabel: 'Platform → API Keys → Create new API key',
  },

  // ── DEV & CODE ─────────────────────────────────────────────────────────
  {
    id: 'linear',
    name: 'Linear',
    description: 'Issues, projetos, ciclos · sincroniza tarefas',
    category: 'dev',
    iconUrl: SIc('linear', '#5e6ad2'),
    status: 'available',
    tokenLabel: 'Personal API Key',
    tokenPlaceholder: 'lin_api_...',
    tokenPrefix: 'lin_api_',
    docsUrl: 'https://linear.app/settings/api',
    docsLabel: 'Criar em Settings → API → Personal API keys',
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Repos, PRs, issues · code intelligence',
    category: 'dev',
    iconUrl: SI('github'),
    status: 'connected-elsewhere', // wired via GitHubStatsCard
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    description: 'Repos, MRs, pipelines',
    category: 'dev',
    iconUrl: SIc('gitlab', '#fc6d26'),
    status: 'available',
    tokenLabel: 'Personal Access Token',
    tokenPlaceholder: 'glpat-...',
    tokenPrefix: 'glpat-',
    docsUrl: 'https://gitlab.com/-/user_settings/personal_access_tokens',
    docsLabel: 'User Settings → Personal Access Tokens (api scope)',
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Issues, sprints, epics',
    category: 'dev',
    iconUrl: SIc('jira', '#0052cc'),
    status: 'available',
    tokenLabel: 'API Token (Atlassian)',
    tokenPlaceholder: 'ATATT3xFfGF0...',
    docsUrl: 'https://id.atlassian.com/manage-profile/security/api-tokens',
    docsLabel: 'Manage account → Security → API tokens',
  },
  {
    id: 'bitbucket',
    name: 'Bitbucket',
    description: 'Repos Atlassian, PRs, pipelines',
    category: 'dev',
    iconUrl: SIc('bitbucket', '#2684ff'),
    status: 'available',
    tokenLabel: 'App Password',
    tokenPlaceholder: 'ATBB...',
    docsUrl: 'https://bitbucket.org/account/settings/app-passwords/',
    docsLabel: 'Personal Settings → App passwords',
  },
  {
    id: 'supabase',
    name: 'Supabase',
    description: 'Postgres, auth, storage, edge functions',
    category: 'dev',
    iconUrl: SIc('supabase', '#3ecf8e'),
    status: 'available',
    tokenLabel: 'Access Token (Management API)',
    tokenPlaceholder: 'sbp_...',
    tokenPrefix: 'sbp_',
    docsUrl: 'https://supabase.com/dashboard/account/tokens',
    docsLabel: 'Account → Access Tokens',
  },
  {
    id: 'vercel',
    name: 'Vercel',
    description: 'Deploys, projetos, domains',
    category: 'dev',
    iconUrl: SI('vercel'),
    status: 'available',
    tokenLabel: 'Access Token',
    tokenPlaceholder: 'vercel_...',
    docsUrl: 'https://vercel.com/account/tokens',
    docsLabel: 'Account Settings → Tokens',
  },
  {
    id: 'netlify',
    name: 'Netlify',
    description: 'Deploys, edge functions, forms',
    category: 'dev',
    iconUrl: SIc('netlify', '#00c7b7'),
    status: 'available',
    tokenLabel: 'Personal Access Token',
    docsUrl: 'https://app.netlify.com/user/applications#personal-access-tokens',
    docsLabel: 'User → Applications → Personal access tokens',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    description: 'Workers, Pages, D1, R2, KV',
    category: 'dev',
    iconUrl: SIc('cloudflare', '#f38020'),
    status: 'available',
    tokenLabel: 'API Token',
    tokenPlaceholder: 'aBc123...',
    docsUrl: 'https://dash.cloudflare.com/profile/api-tokens',
    docsLabel: 'Profile → API Tokens (use "Edit Cloudflare Workers" template)',
  },
  {
    id: 'sentry',
    name: 'Sentry',
    description: 'Error tracking, performance',
    category: 'dev',
    iconUrl: SIc('sentry', '#362d59'),
    status: 'available',
    tokenLabel: 'Auth Token',
    tokenPlaceholder: 'sntrys_...',
    docsUrl: 'https://sentry.io/settings/account/api/auth-tokens/',
    docsLabel: 'Settings → Account → API → Auth Tokens',
  },
  {
    id: 'playwright',
    name: 'Playwright',
    description: 'Browser automation · roda local no Apolo',
    category: 'dev',
    iconUrl: SIc('playwright', '#2ead33'),
    status: 'local-only',
    note: 'Não precisa de token — Apolo orquestra Playwright localmente quando agent precisa',
  },
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    description: 'Incidents, on-call, alerts',
    category: 'dev',
    iconUrl: SIc('pagerduty', '#06ac38'),
    status: 'available',
    tokenLabel: 'API Key',
    docsUrl: 'https://support.pagerduty.com/main/docs/api-access-keys',
    docsLabel: 'User Settings → Create API User Token',
  },

  // ── PRODUCTIVITY ───────────────────────────────────────────────────────
  {
    id: 'notion',
    name: 'Notion',
    description: 'Páginas, databases, comments',
    category: 'productivity',
    iconUrl: SI('notion'),
    status: 'available',
    tokenLabel: 'Internal Integration Secret',
    tokenPlaceholder: 'secret_...',
    tokenPrefix: 'secret_',
    docsUrl: 'https://www.notion.so/profile/integrations',
    docsLabel: 'My integrations → New integration → Copy "Internal Integration Secret"',
  },
  {
    id: 'obsidian',
    name: 'Obsidian',
    description: 'Vault local · Brain MCP do Apolo',
    category: 'productivity',
    iconUrl: SIc('obsidian', '#7c3aed'),
    status: 'local-only',
    note: 'Conexão via Brain MCP (Pedro\'s Brain) — file-based, sem API.',
  },
  {
    id: 'confluence',
    name: 'Confluence',
    description: 'Páginas, spaces, comments',
    category: 'productivity',
    iconUrl: SIc('confluence', '#172b4d'),
    status: 'available',
    tokenLabel: 'API Token (Atlassian)',
    docsUrl: 'https://id.atlassian.com/manage-profile/security/api-tokens',
    docsLabel: 'Mesma chave do Jira — criar em Atlassian → Security → API tokens',
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Arquivos, Docs, Sheets, Slides',
    category: 'productivity',
    iconUrl: SIc('googledrive', '#4285f4'),
    status: 'available',
    flow: 'oauth',
    oauthProvider: 'google',
    oauthStartPath: '/api/oauth/google/start?provider=google-drive',
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Email, threads, drafts, labels',
    category: 'productivity',
    iconUrl: SIc('gmail', '#ea4335'),
    status: 'available',
    flow: 'oauth',
    oauthProvider: 'google',
    oauthStartPath: '/api/oauth/google/start?provider=gmail',
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Eventos, agenda, convites',
    category: 'productivity',
    iconUrl: SIc('googlecalendar', '#4285f4'),
    status: 'available',
    flow: 'oauth',
    oauthProvider: 'google',
    oauthStartPath: '/api/oauth/google/start?provider=google-calendar',
  },
  {
    id: 'microsoft-365',
    name: 'Microsoft 365',
    description: 'Outlook, OneDrive, Teams, Office',
    category: 'productivity',
    iconUrl: SIc('microsoft', '#5e5e5e'),
    status: 'oauth-soon',
  },
  {
    id: 'asana',
    name: 'Asana',
    description: 'Tarefas, projetos, portfolios',
    category: 'productivity',
    iconUrl: SIc('asana', '#f06a6a'),
    status: 'available',
    tokenLabel: 'Personal Access Token',
    docsUrl: 'https://app.asana.com/0/my-apps',
    docsLabel: 'My Profile → Apps → Manage Developer Apps → Personal Access Token',
  },
  {
    id: 'monday',
    name: 'Monday',
    description: 'Work OS, boards, automations',
    category: 'productivity',
    iconUrl: SIc('monday', '#ff3d57'),
    status: 'available',
    tokenLabel: 'API Token v2',
    docsUrl: 'https://developer.monday.com/api-reference/docs/authentication',
    docsLabel: 'Admin → API → API v2 token',
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    description: 'Tarefas, docs, goals, sprints',
    category: 'productivity',
    iconUrl: SIc('clickup', '#7b68ee'),
    status: 'available',
    tokenLabel: 'Personal API Token',
    tokenPlaceholder: 'pk_...',
    tokenPrefix: 'pk_',
    docsUrl: 'https://app.clickup.com/settings/apps',
    docsLabel: 'Settings → Apps → API Token',
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    description: 'File storage, sharing, sync',
    category: 'productivity',
    iconUrl: SIc('dropbox', '#0061ff'),
    status: 'available',
    tokenLabel: 'OAuth Access Token (App console)',
    docsUrl: 'https://www.dropbox.com/developers/apps',
    docsLabel: 'App Console → cole o Generated access token',
  },
  {
    id: 'box',
    name: 'Box',
    description: 'Enterprise file storage',
    category: 'productivity',
    iconUrl: SIc('box', '#0061d5'),
    status: 'oauth-soon',
  },

  // ── COMMUNICATION ──────────────────────────────────────────────────────
  {
    id: 'slack',
    name: 'Slack',
    description: 'Canais, DMs, threads, files',
    category: 'communication',
    iconUrl: SIc('slack', '#4a154b'),
    status: 'available',
    tokenLabel: 'Bot Token (OAuth)',
    tokenPlaceholder: 'xoxb-...',
    tokenPrefix: 'xox',
    docsUrl: 'https://api.slack.com/apps',
    docsLabel: 'Your Apps → OAuth & Permissions → Bot User OAuth Token',
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Servers, channels, bots',
    category: 'communication',
    iconUrl: SIc('discord', '#5865f2'),
    status: 'available',
    tokenLabel: 'Bot Token',
    docsUrl: 'https://discord.com/developers/applications',
    docsLabel: 'Applications → Bot → Token',
  },
  {
    id: 'intercom',
    name: 'Intercom',
    description: 'Tickets, customer messaging',
    category: 'communication',
    iconUrl: SIc('intercom', '#1f8ded'),
    status: 'available',
    tokenLabel: 'Access Token',
    docsUrl: 'https://app.intercom.com/a/apps/_/settings/api-keys',
    docsLabel: 'Settings → Integrations → Developer Hub → New app → Access token',
  },
  {
    id: 'zoom',
    name: 'Zoom',
    description: 'Meetings, recordings, transcripts',
    category: 'communication',
    iconUrl: SIc('zoom', '#0b5cff'),
    status: 'oauth-soon',
  },

  // ── DESIGN ─────────────────────────────────────────────────────────────
  {
    id: 'figma',
    name: 'Figma',
    description: 'Designs, prototypes, components',
    category: 'design',
    iconUrl: SI('figma'),
    status: 'available',
    tokenLabel: 'Personal Access Token',
    tokenPlaceholder: 'figd_...',
    tokenPrefix: 'figd_',
    docsUrl: 'https://www.figma.com/settings',
    docsLabel: 'Settings → Security → Personal access tokens → Generate new',
  },
  {
    id: 'canva',
    name: 'Canva',
    description: 'Templates, designs, brand kits',
    category: 'design',
    iconUrl: SIc('canva', '#00c4cc'),
    status: 'oauth-soon',
  },

  // ── DATA & ANALYTICS ───────────────────────────────────────────────────
  {
    id: 'datadog',
    name: 'Datadog',
    description: 'Logs, traces, metrics, APM',
    category: 'data',
    iconUrl: SIc('datadog', '#632ca6'),
    status: 'available',
    tokenLabel: 'API Key + App Key (separados por :)',
    tokenPlaceholder: 'apikey:appkey',
    docsUrl: 'https://app.datadoghq.com/organization-settings/api-keys',
    docsLabel: 'Organization Settings → API Keys + Application Keys',
  },
  {
    id: 'amplitude',
    name: 'Amplitude',
    description: 'Product analytics, funis, cohorts',
    category: 'data',
    iconUrl: SIc('amplitude', '#1976d2'),
    status: 'available',
    tokenLabel: 'API Key + Secret Key (separados por :)',
    docsUrl: 'https://help.amplitude.com/hc/en-us/articles/360058073772',
    docsLabel: 'Settings → Projects → API Key & Secret Key',
  },
  {
    id: 'posthog',
    name: 'PostHog',
    description: 'Analytics, session replay, feature flags',
    category: 'data',
    iconUrl: SIc('posthog', '#f9bd2b'),
    status: 'available',
    tokenLabel: 'Personal API Key',
    tokenPlaceholder: 'phx_...',
    tokenPrefix: 'phx_',
    docsUrl: 'https://posthog.com/docs/api',
    docsLabel: 'Settings → Personal API keys',
  },
  {
    id: 'mixpanel',
    name: 'Mixpanel',
    description: 'Event analytics, retention, A/B',
    category: 'data',
    iconUrl: SIc('mixpanel', '#7856ff'),
    status: 'available',
    tokenLabel: 'Service Account Username + Secret (separados por :)',
    docsUrl: 'https://developer.mixpanel.com/reference/service-accounts',
    docsLabel: 'Project Settings → Service Accounts',
  },
  {
    id: 'google-bigquery',
    name: 'BigQuery',
    description: 'Data warehouse, SQL, ML',
    category: 'data',
    iconUrl: SIc('googlebigquery', '#669df6'),
    status: 'oauth-soon',
  },
  {
    id: 'hex',
    name: 'Hex',
    description: 'Notebooks, SQL, viz colaborativa',
    category: 'data',
    iconUrl: SIc('hexo', '#0a0a0a'),
    status: 'available',
    tokenLabel: 'Personal API Token',
    docsUrl: 'https://learn.hex.tech/docs/api/api-overview',
    docsLabel: 'Settings → API keys',
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    description: 'Cloud data warehouse',
    category: 'data',
    iconUrl: SIc('snowflake', '#29b5e8'),
    status: 'oauth-soon',
  },

  // ── SALES & CRM ────────────────────────────────────────────────────────
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'CRM, marketing, sales pipeline',
    category: 'sales',
    iconUrl: SIc('hubspot', '#ff7a59'),
    status: 'available',
    tokenLabel: 'Private App Access Token',
    tokenPlaceholder: 'pat-...',
    tokenPrefix: 'pat-',
    docsUrl: 'https://developers.hubspot.com/docs/api/private-apps',
    docsLabel: 'Settings → Integrations → Private Apps',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'CRM enterprise, opportunities',
    category: 'sales',
    iconUrl: SIc('salesforce', '#00a1e0'),
    status: 'oauth-soon',
  },
  {
    id: 'apollo',
    name: 'Apollo',
    description: 'B2B database, outreach, sequences',
    category: 'sales',
    iconUrl: SI('apollographql'),
    status: 'available',
    tokenLabel: 'API Key',
    docsUrl: 'https://app.apollo.io/#/settings/integrations/api',
    docsLabel: 'Settings → Integrations → API → API Key',
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    description: 'Sales CRM, deals, pipelines',
    category: 'sales',
    iconUrl: SIc('pipedrive', '#1a1a1a'),
    status: 'available',
    tokenLabel: 'API Token',
    docsUrl: 'https://app.pipedrive.com/settings/api',
    docsLabel: 'Settings → Personal preferences → API → Your personal API token',
  },

  // ── MARKETING ──────────────────────────────────────────────────────────
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Email marketing, automation',
    category: 'marketing',
    iconUrl: SIc('klaviyo', '#000000'),
    status: 'available',
    tokenLabel: 'Private API Key',
    tokenPlaceholder: 'pk_...',
    tokenPrefix: 'pk_',
    docsUrl: 'https://www.klaviyo.com/account#api-keys-tab',
    docsLabel: 'Account → Settings → API Keys → Create Private API Key',
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Email campaigns, audiences',
    category: 'marketing',
    iconUrl: SIc('mailchimp', '#ffe01b'),
    status: 'available',
    tokenLabel: 'API Key',
    docsUrl: 'https://us1.admin.mailchimp.com/account/api/',
    docsLabel: 'Account → Extras → API keys',
  },
  {
    id: 'ahrefs',
    name: 'Ahrefs',
    description: 'SEO, backlinks, keywords',
    category: 'marketing',
    iconUrl: SIc('ahrefs', '#0090ff'),
    status: 'available',
    tokenLabel: 'API Token',
    docsUrl: 'https://app.ahrefs.com/api-access',
    docsLabel: 'API Access → New token',
  },
  {
    id: 'similarweb',
    name: 'SimilarWeb',
    description: 'Web traffic, competitor analysis',
    category: 'marketing',
    iconUrl: SIc('similarweb', '#092540'),
    status: 'available',
    tokenLabel: 'API Key',
    docsUrl: 'https://account.similarweb.com/api-management',
    docsLabel: 'Account → API Management',
  },

  // ── VOICE & MEETINGS ───────────────────────────────────────────────────
  {
    id: 'fireflies',
    name: 'Fireflies',
    description: 'AI notetaker, transcripts',
    category: 'voice',
    iconUrl: SIc('fireflies', '#ff6b35'),
    status: 'available',
    tokenLabel: 'API Key',
    docsUrl: 'https://app.fireflies.ai/integrations',
    docsLabel: 'Integrations → Fireflies API → Generate',
  },
  {
    id: 'gong',
    name: 'Gong',
    description: 'Revenue intelligence, call recording',
    category: 'voice',
    iconUrl: SIc('gong', '#7d3aed'),
    status: 'available',
    tokenLabel: 'API Access Key + Secret (separados por :)',
    docsUrl: 'https://app.gong.io/company/api',
    docsLabel: 'Company Settings → API (admin only)',
  },
  {
    id: 'granola',
    name: 'Granola',
    description: 'AI meeting notes · app desktop local',
    category: 'voice',
    iconUrl: SIc('apple', '#a3a3a3'),
    status: 'local-only',
    note: 'Granola é um app macOS — integração via filesystem local.',
  },
];

export function getIntegration(id) {
  return INTEGRATIONS.find((i) => i.id === id) || null;
}
